<template>
  <v-card class="mb-4 pa-0 card-container">
    <v-card-title class="pt-2 card-title">
      <v-container class="pa-0 justify-space-between pr-5" fluid fill-height>
        <span class="pa-1 title">Usuários ativos</span>
        <chart-type-toggle-button :type.sync="chartType" />
      </v-container>
      <v-card-subtitle class="pa-0">
        <v-container class="pa-0 ma-0" fluid fill-height>
          <span class="pa-1 subtitle mf-subtitle-1 local-subtitle">
            Quantidade de usuários que interagiram com o aplicativo, dentro de um período. Os logados são aqueles que se cadastram, logam e utilizam o
            aplicativo. Os não logados são aqueles que utilizam o aplicativo sem realizar o login.
          </span>
        </v-container>
      </v-card-subtitle>
    </v-card-title>
    <mf-loading class="pa-0" loader="faturamento" loader-type="chart">
      <v-container fluid class="pa-3">
        <apex-chart v-if="series[0].data.length > 0 || series[1].data.length > 0" height="350" :options="chartOptions" :series="series" />
        <chart-navigator
          v-if="series[0].data.length > 0 || series[1].data.length > 0"
          :selectedPeriod="chartPeriod"
          :chart-index="chartIndex"
          :metrics-length="metricsLength"
          :metrics-period="metricsPeriod"
          @navigateTo="navigateTo"
        />
      </v-container>
      <empty-chart v-if="series[0].data.length === 0 && series[1].data.length === 0" class="" />
    </mf-loading>
  </v-card>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import { QUERY_GET_APP_ACTIVE_USERS } from '../../graphql'
import { colors } from '@/config/colors.js'
import ChartTypeToggleButton from '@/components/ChartTypeToggleButton.vue'
import EmptyChart from '@/modules/mobile/desempenho/views/components/EmptyChart'
import ChartNavigator from '@/components/ChartNavigator'
import getAppMetricsNavigatorPeriod from '@/services/getAppMetricsNavigatorPeriod'
import moment from 'moment'

export default {
  components: {
    ApexChart: VueApexCharts,
    ChartTypeToggleButton: ChartTypeToggleButton,
    EmptyChart,
    ChartNavigator,
  },
  props: {
    metricsPeriod: String,
  },
  data: () => ({
    chartType: 'bar',
    chartIndex: 0,
  }),
  computed: {
    chartOptions() {
      return {
        chart: {
          height: 350,
          type: this.chartType,
          stacked: this.chartType === 'bar',
          zoom: {
            enabled: false,
          },
          toolbar: {
            show: false,
          },
          background: '#fff',
        },
        annotations: {
          position: 'back',
          xaxis: this.annotations,
        },
        plotOptions: {
          bar: {
            columnWidth: this.metricsPeriod === 'MONTHLY' ? '40px' : '20px',
            borderRadius: 3,
            dataLabels: {
              position: 'top',
            },
          },
        },
        fill: {
          opacity: 1,
          type: 'solid',
        },
        grid: {
          padding: {
            top: 10,
            right: 10,
            bottom: 10,
            left: 10,
          },
        },
        colors: [colors[0], colors[1]],
        legend: {
          horizontalAlign: 'left',
          markers: {
            width: 7,
            height: 7,
            radius: 3.5,
          },
          offsetX: 5,
          offsetY: -10,
          onItemClick: {
            toggleDataSeries: false,
          },
          formatter(seriesName, { seriesIndex, w }) {
            if (seriesIndex === 0) {
              return seriesName
            }
            return `
              <span>${seriesName}</span><span class="ml-3" style="height: 5px; width: 1px; border: 1px solid #0FC94D"></span><span class="ml-2">Push</span>
            `
          },
        },
        markers: {
          size: 2,
          colors: [colors[0], colors[1]],
          strokeColors: [colors[0], colors[1]],
          strokeWidth: 2,
          strokeOpacity: 1,
          strokeDashArray: 0,
          fillOpacity: 1,
          shape: 'circle',
          radius: 2,
          showNullDataPoints: true,
          hover: {
            sizeOffset: 2,
          },
        },
        tooltip: {
          custom: ({ series, seriesIndex, dataPointIndex, w }) => {
            const loggedUsers = series[0][dataPointIndex]
            const unloggedUsers = series[1][dataPointIndex]
            const totalUsers = unloggedUsers + loggedUsers
            const loggedUsersPercent = totalUsers === 0 ? '0%' : `${Math.round((loggedUsers / totalUsers) * 100)}%`
            const unloggedUsersPercent = totalUsers === 0 ? '0%' : `${Math.round((unloggedUsers / totalUsers) * 100)}%`
            return `
              <v-card class="pr-5 pl-5 pt-2 pb-2">
                <v-card-title class="pa-0 card-title">
                  <span class="title-medium" style="font-weight: bold">
                    ${this.chartRange[dataPointIndex]} de ${this.chartPeriod}
                  </span>
                </v-card-title>
                </br>
                <span class="marca-1-text">${totalUsers.toLocaleString()} usuários ativos</span>
                </br>
                </br>
                <span class="marca-1-text">${loggedUsers.toLocaleString()}</span>
                <span class="cinza-d20-text"> usuários logados</span>
                </br>
                <span class="subtitle" style="font-size: 13px">${loggedUsersPercent} dos acessos</span>
                </br>
                <span class="marca-1-text">${unloggedUsers.toLocaleString()}</span>
                <span class="cinza-d20-text"> usuários não logados</span>
                </br>
                <span class="subtitle" style="font-size: 13px">${unloggedUsersPercent} dos acessos</span>
              </v-card>
            `
          },
        },
        dataLabels: {
          enabled: true,
          position: 'top',
          offsetY: this.chartType === 'bar' ? -20 : -10,
          style: {
            fontSize: '12px',
            colors: ['#F9FAFB'],
            fontWeight: 'normal',
          },
          background: {
            enabled: true,
            foreColor: '#969EA7',
            padding: 4,
            borderRadius: 2,
          },
          formatter: (val, { seriesIndex, dataPointIndex, w }) => {
            let formattedValue
            const labelValue =
              val === null ? '' : this.chartType === 'bar' ? w.config.series[0].data[dataPointIndex] + w.config.series[1].data[dataPointIndex] : val
            formattedValue = labelValue >= 1000 ? `${(labelValue / 1000).toFixed(1).toLocaleString()}k` : labelValue
            // lib está com um bug, não aparece o label em gráficos empilhados quando a série de cima é zero, deixei as labels nas duas colunas e escondi na primeira quando a segunda não for 0
            if (w.config.series[1].data[dataPointIndex] !== 0 && this.chartType === 'bar' && seriesIndex === 0) formattedValue = ''
            return formattedValue
          },
        },
        stroke: {
          curve: 'smooth',
          colors: [colors[0], colors[1]],
          width: this.chartType === 'bar' ? 2 : 3,
        },
        xaxis: {
          categories: this.chartRange,
          category: 'numeric',
          labels: {
            style: {
              colors: '#969EA7',
            },
          },
        },
        yaxis: {
          labels: {
            formatter: function (val, index) {
              if (!val) return
              return val.toLocaleString()
            },
          },
        },
      }
    },
    series() {
      if (!this.appActiveUsers || this.appActiveUsers.length === 0) {
        return [
          {
            data: [],
          },
          {
            data: [],
          },
        ]
      }
      const currentSelectedMetrics = [...this.appActiveUsers[this.chartIndex].metrics].reverse()
      const series = this.chartRange.reduce(
        (acc, date) => {
          const [metric] = currentSelectedMetrics.filter((metric) =>
            this.metricsPeriod === 'MONTHLY'
              ? parseInt(moment(metric.date).format('DD')) === date
              : moment(metric.month).locale('pt-br').format('MMMM') === date.toLowerCase()
          )
          if (metric !== undefined) {
            acc[0].data.push(metric.logged_users_count)
            acc[1].data.push(metric.unlogged_users_count)
          } else if (
            metric === undefined &&
            !(
              this.chartIndex === 0 &&
              (this.metricsPeriod === 'MONTHLY' ? parseInt(date) > parseInt(moment().format('DD')) : moment(date, 'MMMM', 'pt-br').month() > moment().month())
            )
          ) {
            acc[0].data.push(0)
            acc[1].data.push(0)
          } else if (
            metric === undefined &&
            this.chartIndex === 0 &&
            (this.metricsPeriod === 'MONTHLY' ? parseInt(date) > parseInt(moment().format('DD')) : moment(date, 'MMMM', 'pt-br').month() > moment().month())
          ) {
            acc[0].data.push(null)
            acc[1].data.push(null)
          }
          return acc
        },
        [
          {
            name: 'Logados',
            data: [],
          },
          {
            name: 'Não logados',
            data: [],
          },
        ]
      )
      return series
    },
    annotations() {
      let xaxisPostion
      if (!this.appActiveUsers || this.appActiveUsers.length === 0) return []
      const currentSelectedMetrics = [...this.appActiveUsers[this.chartIndex].metrics].reverse()
      return currentSelectedMetrics
        .filter((metric) => metric.push_campaign)
        .map((metric) => {
          if (this.metricsPeriod === 'MONTHLY') {
            xaxisPostion = parseInt(moment(metric.date).format('DD'))
          } else {
            xaxisPostion = moment(metric.month).locale('pt-br').format('MMMM')
            xaxisPostion = `${xaxisPostion.substring(0, 1).toUpperCase()}${xaxisPostion.substring(1, xaxisPostion.length)}`
          }
          return {
            x: xaxisPostion,
            strokeDashArray: 0,
            borderColor: '#0FC94D',
          }
        })
    },
    chartRange() {
      let i
      let chartRange = []
      if (this.metricsPeriod === 'YEARLY') {
        chartRange = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro']
      } else {
        for (i = 1; i <= this.chartMax; i++) {
          chartRange.push(i)
        }
      }
      return chartRange
    },
    metricsLength() {
      if (!this.appActiveUsers) return 0
      return this.appActiveUsers.length - 1
    },
    chartMax() {
      const currentPeriod = this.metricsPeriod === 'MONTHLY' ? this.appActiveUsers[this.chartIndex].metrics[0].date : this.appActiveUsers[this.chartIndex].year
      const { chartMax } = getAppMetricsNavigatorPeriod(this.metricsPeriod, currentPeriod)
      return chartMax
    },
    chartPeriod() {
      const currentPeriod = this.metricsPeriod === 'MONTHLY' ? this.appActiveUsers[this.chartIndex].metrics[0].date : this.appActiveUsers[this.chartIndex].year
      const { navigatorPeriod } = getAppMetricsNavigatorPeriod(this.metricsPeriod, currentPeriod)
      return navigatorPeriod
    },
  },
  apollo: {
    appActiveUsers: {
      query: QUERY_GET_APP_ACTIVE_USERS,
      variables() {
        return { metricsPeriod: this.metricsPeriod }
      },
    },
  },
  watch: {
    metricsPeriod() {
      this.chartIndex = 0
    },
  },
  methods: {
    navigateTo(newIndex) {
      this.chartIndex = newIndex
    },
  },
}
</script>

<style lang="scss" scoped>
.card-container {
  min-height: 400px;
}
.local-subtitle {
  width: 80%;
  word-break: normal;
}
::v-deep .marca-1-text {
  color: $marca-1;
  font-weight: bold;
}
::v-deep .cinza-d20-text {
  color: $cinza-d20;
  font-weight: bold;
}
</style>
